import { FormEvent } from 'react'

import { trackOnboardingCompleted } from '../../lib/posthogEvents'
import { supabase } from '../../lib/supabase'

import { OnboardingData, AIProvider } from './types'

// Handle form submission
export async function handleSubmit(
  e: FormEvent,
  data: OnboardingData,
  resumeFilePath: string | null,
  resumeMdPath: string | null,
  aiProvider: AIProvider,
  aiMemoryFilePath: string | null,
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  navigate: (path: string) => void
) {
  e.preventDefault()
  setSaving(true)
  setError(null)

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      navigate('/login')
      return
    }

    // Use already uploaded resume file path, or null if no resume was uploaded
    const finalResumeFilePath = resumeFilePath

    // Save profile data to user_profile
    const profilePayload = {
      user_id: user.id,
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone || null,
      email: data.email || user.email,
      resume_file_path: finalResumeFilePath,
      resume_md_path: resumeMdPath,
      ai_provider: aiProvider || 'none',
      ai_memory_file_path: aiMemoryFilePath,
    }

    const { error: profileError } = await supabase
      .from('user_profile')
      .upsert(profilePayload, {
        onConflict: 'user_id',
      })

    if (profileError) {
      throw profileError
    }

    // Save onboarding status to user_onboarding
    const onboardingStatusPayload = {
      user_id: user.id,
      completed_at: new Date().toISOString(),
    }

    // Set started_at if not already set
    const { data: existingOnboarding } = await supabase
      .from('user_onboarding')
      .select('started_at')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!existingOnboarding?.started_at) {
      onboardingStatusPayload.started_at = new Date().toISOString()
    }

    const { error: onboardingError } = await supabase
      .from('user_onboarding')
      .upsert(onboardingStatusPayload, {
        onConflict: 'user_id',
      })

    if (onboardingError) {
      throw onboardingError
    }

    trackOnboardingCompleted()

    // Navigate to main page
    navigate('/')
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    setError(errorMessage || 'Failed to save onboarding data')
  } finally {
    setSaving(false)
  }
}

// Handle skip action
export async function handleSkip(
  data: OnboardingData,
  resumeFilePath: string | null,
  resumeMdPath: string | null,
  aiProvider: AIProvider,
  aiMemoryFilePath: string | null,
  navigate: (path: string) => void
) {
  // When skipping, mark onboarding as completed so user doesn't get redirected back
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      navigate('/login')
      return
    }

    // Save any profile data that was entered
    if (data.firstName || data.lastName || data.phone || resumeFilePath || resumeMdPath || aiMemoryFilePath) {
      const profilePayload = {
        user_id: user.id,
        first_name: data.firstName || null,
        last_name: data.lastName || null,
        phone: data.phone || null,
        email: data.email || user.email,
        resume_file_path: resumeFilePath || null,
        resume_md_path: resumeMdPath || null,
        ai_provider: aiProvider || 'none',
        ai_memory_file_path: aiMemoryFilePath || null,
      }

      const { error: profileError } = await supabase
        .from('user_profile')
        .upsert(profilePayload, {
          onConflict: 'user_id',
        })

      if (profileError) {
        console.error('Error saving profile on skip:', profileError)
      }
    }

    // Mark onboarding as completed even when skipping
    const onboardingStatusPayload = {
      user_id: user.id,
      completed_at: new Date().toISOString(),
    }

    // Set started_at if not already set
    const { data: existingOnboarding } = await supabase
      .from('user_onboarding')
      .select('started_at')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!existingOnboarding?.started_at) {
      onboardingStatusPayload.started_at = new Date().toISOString()
    }

    const { error: onboardingError } = await supabase
      .from('user_onboarding')
      .upsert(onboardingStatusPayload, {
        onConflict: 'user_id',
      })

    if (onboardingError) {
      console.error('Error saving onboarding skip:', onboardingError)
      // Still navigate even if save fails - user can complete later
    }

    trackOnboardingCompleted()

    // Navigate to main page
    navigate('/')
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    console.error('Error in handleSkip:', errorMessage)
    // Still navigate even if error occurs
    navigate('/')
  }
}
